/**
 * This file contains pure, stateless utility functions for tasks like comparing
 * addresses, formatting street etc.
 *
 * It isolates common, reusable logic that isn't tied to a specific component's
 * state or the map library itself. This makes these functions easy to test
 * and use anywhere in the application that needs to manipulate address data.
 */
import { LngLat } from 'maplibre-gl';

const withoutWhitespace = s => {
  return s.replace(/\s+/g, '');
};

const comparable = s => {
  return withoutWhitespace(String(s)).toLowerCase();
};

export const stringCompare = (s1, s2) => {
  return comparable(s1) === comparable(s2);
};

export const getStreetAddress = address => {
  return `${address.street} ${address.number || address.houseNumber || ''}`;
};

export const formatParcelShopAddress = parcelShop => {
  if (!parcelShop?.address) return '';
  const street = getStreetAddress(parcelShop.address);
  const city = `${parcelShop.address.zipCode} ${parcelShop.address.city}`;
  return `${street}, ${city}`;
};

export const parcelShopToLngLat = parcelShop => {
  return new LngLat(parcelShop.address.longitude, parcelShop.address.latitude);
};

export const addressEqualsParcelShop = (
  address,
  parcelShop,
  useCoordinateMatching = false
) => {
  const COORDINATE_MATCH_EPSILON = 0.0001;

  const addressMatch =
    comparable(address.name).includes(comparable(parcelShop.name)) &&
    stringCompare(
      getStreetAddress(address),
      getStreetAddress(parcelShop.address)
    ) &&
    stringCompare(address.postcode, parcelShop.address.zipCode) &&
    stringCompare(address.city, parcelShop.address.city);

  const coordinateMatch = (() => {
    if (!useCoordinateMatching || !address.coordinates) return false;

    const source = LngLat.convert(address.coordinates);
    const target = parcelShopToLngLat(parcelShop);

    return (
      Math.abs(source.lng - target.lng) <= COORDINATE_MATCH_EPSILON &&
      Math.abs(source.lat - target.lat) <= COORDINATE_MATCH_EPSILON
    );
  })();

  return addressMatch || coordinateMatch;
};

export const statusAddressToParcelShop = statusAddress => {
  return {
    parcelShopId: '',
    name: statusAddress.name,
    address: {
      countryCode: statusAddress.country,
      city: statusAddress.city,
      houseNumber: statusAddress.number,
      latitude: statusAddress.coordinates?.lat,
      longitude: statusAddress.coordinates?.lng,
      street: statusAddress.street,
      zipCode: statusAddress.postcode
    }
  };
};

export const getParcelShopFromStatusAddress = (
  statusAddress,
  nearParcelShops
) => {
  return (
    nearParcelShops?.find(it =>
      addressEqualsParcelShop(statusAddress, it, true)
    ) ?? statusAddressToParcelShop(statusAddress)
  );
};

export const hasCoordinates = address => {
  return Boolean(
    address?.coordinates &&
      typeof address.coordinates.lat === 'number' &&
      typeof address.coordinates.lng === 'number'
  );
};
